import React from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Icon,
  VStack,
  Flex,
  Badge,
  Circle,
} from '@chakra-ui/react';
import { FolderOpen, PaperPlaneTilt, Buildings, ArrowRight, User, Receipt } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import SecaoClienteLayout from '../layout';

interface ClienteData {
  id: number;
  guid: string;
  nome: string;
  email: string;
  empresas?: Array<{
    id: number;
    razao_social: string;
    nome_fantasia: string;
    cnpj: string | null;
  }>;
}

const SecaoClienteDashboard = () => {
  const navigate = useNavigate();

  // Pegar dados do cliente do localStorage
  const clienteDataStr = localStorage.getItem('cliente_data');
  const clienteData: ClienteData | null = clienteDataStr ? JSON.parse(clienteDataStr) : null;

  const totalEmpresas = clienteData?.empresas?.length || 0;

  const menuCards = [
    {
      title: 'Dossiê',
      description:
        'Acesse os dossiês das suas empresas com informações fiscais, folha de pagamento e muito mais',
      icon: FolderOpen,
      path: '/dossie',
      color: 'blue',
      badge: totalEmpresas > 0 ? `${totalEmpresas} empresas` : null,
    },
    {
      title: 'Guias e Impostos',
      description: 'Consulte e baixe as guias de pagamento de impostos das suas empresas',
      icon: Receipt,
      path: '/guias',
      color: 'purple',
      badge: null,
    },
    {
      title: 'Envio de Documentos',
      description: 'Envie documentos e arquivos de forma segura para o escritório',
      icon: PaperPlaneTilt,
      path: '/documentos',
      color: 'green',
      badge: null,
    },
  ];

  // Pegar hora do dia para saudação
  const getSaudacao = () => {
    const hora = new Date().getHours();
    if (hora < 12) return 'Bom dia';
    if (hora < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  return (
    <SecaoClienteLayout>
      <VStack spacing={{ base: 6, md: 8 }} align="stretch">
        {/* Welcome Section */}
        <Box
          bg="linear-gradient(135deg, #1E3A5F 0%, #2D5A87 100%)"
          borderRadius={{ base: 'xl', md: '2xl' }}
          p={{ base: 5, md: 8 }}
          color="white"
          position="relative"
          overflow="hidden"
        >
          {/* Background decoration */}
          <Box
            position="absolute"
            right={{ base: '-20px', md: '20px' }}
            top={{ base: '-20px', md: '50%' }}
            transform={{ md: 'translateY(-50%)' }}
            opacity={0.1}
          >
            <Icon as={User} boxSize={{ base: '120px', md: '180px' }} />
          </Box>

          <VStack align="start" spacing={2} position="relative" zIndex={1}>
            <Text fontSize={{ base: 'sm', md: 'md' }} opacity={0.9}>
              {getSaudacao()},
            </Text>
            <Heading size={{ base: 'lg', md: 'xl' }}>
              {clienteData?.nome?.split(' ')[0] || 'Cliente'}!
            </Heading>
            <Text fontSize={{ base: 'sm', md: 'md' }} opacity={0.8} maxW="500px">
              Bem-vindo à sua área exclusiva. Aqui você pode acessar os dossiês das suas empresas e
              enviar documentos.
            </Text>
          </VStack>
        </Box>

        {/* Stats Card */}
        {totalEmpresas > 0 && (
          <Card size="sm" maxW={{ base: '100%', md: '280px' }}>
            <CardBody>
              <Flex align="center" gap={3}>
                <Circle size={{ base: '40px', md: '48px' }} bg="blue.50">
                  <Icon as={Buildings} boxSize={{ base: 5, md: 6 }} color="blue.500" />
                </Circle>
                <Box>
                  <Text
                    fontSize={{ base: '2xl', md: '3xl' }}
                    fontWeight="bold"
                    color="gray.800"
                    lineHeight="1"
                  >
                    {totalEmpresas}
                  </Text>
                  <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500">
                    {totalEmpresas === 1 ? 'Empresa' : 'Empresas'}
                  </Text>
                </Box>
              </Flex>
            </CardBody>
          </Card>
        )}

        {/* Menu Cards */}
        <Box>
          <Heading size={{ base: 'sm', md: 'md' }} color="gray.700" mb={4}>
            Acesso Rápido
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 4, md: 6 }}>
            {menuCards.map(card => (
              <Card
                key={card.path}
                cursor="pointer"
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: 'xl',
                  borderColor: `${card.color}.200`,
                }}
                borderWidth="1px"
                borderColor="gray.100"
                onClick={() => navigate(card.path)}
              >
                <CardBody p={{ base: 5, md: 6 }}>
                  <Flex direction={{ base: 'column', sm: 'row' }} gap={4}>
                    <Flex
                      align="center"
                      justify="center"
                      p={{ base: 3, md: 4 }}
                      borderRadius="xl"
                      bg={`${card.color}.50`}
                      alignSelf={{ base: 'flex-start', sm: 'center' }}
                    >
                      <Icon
                        as={card.icon}
                        boxSize={{ base: 7, md: 8 }}
                        color={`${card.color}.500`}
                        weight="duotone"
                      />
                    </Flex>
                    <Box flex={1}>
                      <Flex align="center" gap={2} mb={1}>
                        <Heading size={{ base: 'sm', md: 'md' }} color="gray.800">
                          {card.title}
                        </Heading>
                        {card.badge && (
                          <Badge colorScheme={card.color} fontSize="xs" borderRadius="full">
                            {card.badge}
                          </Badge>
                        )}
                      </Flex>
                      <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" noOfLines={2}>
                        {card.description}
                      </Text>
                    </Box>
                    <Flex align="center" display={{ base: 'none', sm: 'flex' }}>
                      <Icon as={ArrowRight} boxSize={5} color="gray.400" />
                    </Flex>
                  </Flex>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      </VStack>
    </SecaoClienteLayout>
  );
};

export default SecaoClienteDashboard;
